<ng-container *ngIf="!isSmallScreen; else mobileCards">
  <ng-container *ngIf="!isPanelCollapsed; else collapsedCards">
    <p-splitter
      layout="horizontal"
      [panelSizes]="splitterSizes"
      [minSizes]="splitterMinSizes"
      [gutterSize]="splitterGutterSize"
      stateStorage="local"
      [stateKey]="splitterStateKey"
      styleClass="h-full w-full"
      (onResizeEnd)="onSplitterResize($event)">
      <ng-template pTemplate="panel">
        <div class="flex h-full min-h-0 flex-col border-r border-surface-200">
          <div class="flex items-center justify-between border-b border-surface-200 
                px-[1px] py-[1px]">
            <span class="text-xs font-semibold uppercase tracking-widest
             text-slate-500">Categories</span>
            <button
              type="button" class="inline-flex h-7 w-7 items-center justify-center 
                rounded border border-slate-200 text-slate-500 transition
                 hover:border-slate-300 hover:text-slate-700"
              (click)="collapsePanel()"
              aria-label="Collapse categories panel">
              <span class="material-icons text-base" aria-hidden="true">
                    chevron_left</span>
            </button>
          </div>
          <div class="flex-1 overflow-auto p-0 m-0">
            <p-tree
              styleClass="categories-tree"
              [value]="categoryTreeNodes"
              selectionMode="checkbox"
              [propagateSelectionDown]="true"
              [propagateSelectionUp]="true"
              [selection]="selectedTreeNodes"
              (selectionChange)="onTreeSelectionChange($event ?? null)"
              [filter]="true"
              filterMode="lenient"
              filterPlaceholder="Filter categories">
            </p-tree>
          </div>
        </div>
      </ng-template>
      <ng-template pTemplate="panel">
        <div class="relative h-full overflow-y-auto">
          <ng-container *ngTemplateOutlet="cardsContent"></ng-container>
        </div>
      </ng-template>
    </p-splitter>
  </ng-container>
</ng-container>

<ng-template #mobileCards>
  <ng-container *ngTemplateOutlet="cardsContent"></ng-container>
</ng-template>

<ng-template #collapsedCards>
  <div class="relative h-full overflow-y-auto">
    <button
      type="button"
      class="absolute left-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center
         rounded border border-slate-200 bg-white text-slate-500 shadow-sm 
         transition hover:border-slate-300 hover:text-slate-700"
      (click)="expandPanel()"
      aria-label="Expand categories panel">
      <span class="material-icons text-base" aria-hidden="true">chevron_right</span>
    </button>
    <ng-container *ngTemplateOutlet="cardsContent"></ng-container>
  </div>
</ng-template>
<ng-template #cardsContent>
  <div class="columns-1 sm:columns-2 lg:columns-3 gap-4 p-1 [column-fill:_balance]">

    <ng-container *ngFor="let card of cards; trackBy: trackByCardId">
      <div
        class="mb-4 break-inside-avoid border-2 border-primary rounded-md bg-white
             dark:bg-gray-800"
        *ngIf="calculateItemCount(card._id) > 0">
        <!-- card header -->
            <div
              class="h-7 font-bold border-b border-[var(--borderColor)] text-black
                 dark:text-white bg-primary flex items-center justify-between
                  relative px-1"
              [style.background-color]="card.Color">
          <button
            *ngIf="isSelectMode"
            type="button"
            class="category-select-toggle"
            [ngClass]="{ 'category-select-toggle--active': 
                areAllNotesSelectedForCard(card._id) }"
            (click)="toggleSelectAllForCard(card._id, $event)">
            <i
              class="pi"
              [ngClass]="areAllNotesSelectedForCard(card._id) ? 
                'pi-check-square' : 'pi-clone'"></i>
          </button>
          
          <div
            class="category-card-title flex-1 max-w-full overflow-hidden
                 whitespace-nowrap text-ellipsis text-center cursor-pointer"
            [class.pl-2]="!isSelectMode"
            [class.pl-7]="isSelectMode"
            [class.pr-16]="card.Type === 'category'"
            [class.pr-8]="card.Type !== 'category'"
            [title]="card.Title"
            (click)="clickCardHeader(card)">
            {{ card.Title }}
          </div>
          
          <!-- Expand/Collapse toggle button -->
          <button
            type="button"
            class="category-expand-toggle"
            [attr.aria-label]="card.isExpanded ? 'Collapse card' : 'Expand card'"
            [attr.aria-expanded]="card.isExpanded"
            (click)="toggleCardExpanded(card, $event)">
            <span class="material-symbols-outlined text-base transition-transform duration-200"
                  [style.transform]="card.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'">
              expand_more
            </span>
          </button>
          
          <button
            *ngIf="card.Type === 'category'"
            type="button"
            class="category-add-button"
            aria-label="Create note in category"
            (click)="createNoteForCategory(card._id, $event)">
            +
          </button>
        </div>

        <!-- card content -->
        <div class="max-h-72 overflow-y-auto transition-all duration-300 ease-in-out"
             *ngIf="card.isExpanded"
             [@slideDown]>
          <table class="w-full">
            <tbody>
              <tr
                class="h-8 hover:bg-primary-400 cursor-pointer bg-primary-100"
                *ngFor="let note of getNotesForCard(card._id); trackBy: trackByNoteId"
                [ngClass]="{
                  'bg-[var(--project5Color)]': note.ProjectName === 'Work',
                  'bg-[var(--project1Color)]': note.ProjectName === 'NotesApp',
                  'bg-[var(--project2Color)]': note.ProjectName === 'Vivi/Olivia',
                  'bg-[var(--project3Color)]': note.ProjectName === 'Shopping',
                  'font-bold': note.Status === 'Next Action',
                  'italic text-[var(--textColorFaded)]':
                    note.Status === 'Completed' ||
                    note.Status === 'Obsolete' ||
                    note.Status === 'Cancelled' ||
                    note.Status === 'Expired',
                  'ring-2 ring-primary-600': isNoteSelected(note._id),
                  'select-mode-row': isSelectMode
                }">
                <td
                  class="px-2 border border-[var(--borderColor)] w-7 max-w-7"
                  [ngClass]="{
                    '!bg-orange-300': note.syncStatus === 'updated',
                    '!bg-red-600': note.syncStatus === 'new',
                    '!line-through text-red-600': note.syncStatus === 'deleted'
                  }">
                  <a *ngIf="!isSelectMode; else selectMode" id="EditLink"
                     [routerLink]="['/edit', note?._id]"
                     (click)="$event.stopPropagation()">
                    <i class="pi pi-pen-to-square"></i>
                  </a>

                  <ng-template #selectMode>
                    <button
                      type="button"
                      class="flex items-center justify-center rounded border
                         border-[var(--borderColor)] bg-white text-primary shadow-sm
                          transition hover:-translate-y-0.5 hover:shadow"
                      aria-label="Toggle selection"
                      (click)="toggleSelection(note, $event)">
                      <i
                        class="pi"
                        [ngClass]="{
                          'pi-check-square text-primary-600': isNoteSelected(note?._id),
                          'pi-square text-gray-400': !isNoteSelected(note?._id)
                        }"></i>
                    </button>
                  </ng-template>
                </td>
                <td
                  class="w-96 whitespace-normal px-2 border border-[var(--borderColor)] cursor-pointer"
                  (click)="clickRow(note)"
                  [ngClass]="{
                    '!line-through text-red-600': note.Status === 'Deleted' ||
                         note.syncStatus === 'deleted',
                    '!text-red-500': note.syncStatus !== 'synced' && 
                        note.syncStatus !== 'deleted'
                  }">
                  {{ this.getNoteYearPrefix(note) }}
                  {{ note.Title }}
                  {{ this.getAttachmentFlag(note) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ng-container>

  </div>
</ng-template>
